import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Eye, Users, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useSupabaseData } from '@/contexts/SupabaseDataContext';
import EvaluationDetailsModal from '@/components/evaluation/EvaluationDetailsModal';

const Dashboard = ({ onNavigate }) => {
  const { students: allStudents, evaluations: allEvaluations, loading } = useSupabaseData();
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [filteredEvaluations, setFilteredEvaluations] = useState([]);
  const [activePolo, setActivePolo] = useState('Todos');
  const [selectedEvaluation, setSelectedEvaluation] = useState(null);
  const [isEvaluationModalOpen, setIsEvaluationModalOpen] = useState(false);
  const polos = ['Todos', 'Osasco', 'Villa Lobos', 'Belenzinho', 'Guarulhos'];

  useEffect(() => {
    const studentsToShow = activePolo === 'Todos'
      ? allStudents
      : allStudents.filter(student => student.location === activePolo);
    setFilteredStudents(studentsToShow);

    const studentIdsToShow = studentsToShow.map(s => s.id);
    const evaluationsToShow = allEvaluations.filter(ev => studentIdsToShow.includes(ev.student_id));
    setFilteredEvaluations(evaluationsToShow.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
  }, [activePolo, allStudents, allEvaluations]);

  const getStudentCountByPolo = (polo) => {
    if (polo === 'Todos') {
      return allStudents.length;
    }
    return allStudents.filter(student => student.location === polo).length;
  };

  const handleViewEvaluation = (evaluation) => {
    setSelectedEvaluation(evaluation);
    setIsEvaluationModalOpen(true);
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 flex flex-col items-center text-center"
        >
          <img src="https://storage.googleapis.com/hostinger-horizons-assets-prod/6079d89c-cd3e-4a30-bc52-e93fa828bbc4/99e204f6dcbf232bedd9946e22a830f8.png" alt="Pedalando pela Inclusão Logo" className="w-48 h-auto mb-4" />
          <h1 className="text-4xl font-bold text-white mb-2">
            Pedalando pela Inclusão
          </h1>
          <p className="text-xl text-purple-200">
            Sistema de Avaliação Psicomotora
          </p>
        </motion.div>

        <Tabs value={activePolo} onValueChange={setActivePolo} className="mb-8">
          <TabsList className="flex flex-wrap h-auto justify-center bg-white/10 border-white/20">
            {polos.map(polo => (
              <TabsTrigger key={polo} value={polo} className="text-white data-[state=active]:bg-white/20 data-[state=active]:text-white">
                {polo} {polo !== 'Todos' ? `(${getStudentCountByPolo(polo)})` : ''}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 }}>
            <Card className="bg-gradient-to-br from-purple-500 to-indigo-600 border-none">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-white">Total de Alunos</CardTitle>
                <Users className="h-5 w-5 text-white/80" />
              </CardHeader>
              <CardContent>
                <div className="text-4xl font-bold text-white">{loading ? <Loader2 className="h-8 w-8 animate-spin" /> : allStudents.length}</div>
                <p className="text-xs text-white/80">Alunos em todos os polos</p>
              </CardContent>
            </Card>
          </motion.div>
        </div>


        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card className="bg-transparent border-white/20 h-full">
              <CardHeader className="flex flex-row justify-between items-center">
                <CardTitle className="text-white">Alunos Cadastrados ({activePolo})</CardTitle>
                <Button
                  onClick={() => onNavigate('registration')}
                  size="sm"
                  className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Novo
                </Button>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center items-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-white" />
                  </div>
                ) : filteredStudents.length > 0 ? (
                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {filteredStudents.map((student) => (
                      <div
                        key={student.id}
                        className="flex justify-between items-center p-3 rounded-lg bg-white/5 border border-white/10"
                      >
                        <div>
                          <p className="text-white font-medium">{student.student_name}</p>
                          <p className="text-white/60 text-sm">{student.age} anos - {student.location}</p>
                          <p className="text-white/60 text-sm">Instrutor: {student.responsible_name || 'Não especificado'}</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onNavigate('studentDashboard', student)}
                          className="text-white hover:bg-white/10"
                        >
                          <Eye className="mr-2 h-4 w-4" /> Ver
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-white/60 text-center py-8">
                    Nenhum aluno encontrado para "{activePolo}".
                  </p>
                )}
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
          >
            <Card className="bg-transparent border-white/20 h-full">
              <CardHeader>
                <CardTitle className="text-white">Avaliações Recentes ({activePolo})</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center items-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-white" />
                  </div>
                ) : filteredEvaluations.length > 0 ? (
                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {filteredEvaluations.map((evaluation) => (
                      <div
                        key={evaluation.id}
                        className="flex justify-between items-center p-3 rounded-lg bg-white/5 border border-white/10"
                      >
                        <div>
                          <p className="text-white font-medium">{evaluation.studentName}</p>
                          <p className="text-white/60 text-sm">{new Date(evaluation.created_at).toLocaleDateString('pt-BR')}</p>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-white font-bold">{evaluation.totalScore}/21</p>
                            <p className="text-white/60 text-[10px] leading-tight max-w-[120px]">{evaluation.interpretation}</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewEvaluation(evaluation)}
                            className="text-white hover:bg-white/10"
                          >
                            <Eye className="mr-2 h-4 w-4" /> Ver
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-white/60 text-center py-8">
                    Nenhuma avaliação encontrada para "{activePolo}".
                  </p>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
      <EvaluationDetailsModal
        isOpen={isEvaluationModalOpen}
        onClose={() => setIsEvaluationModalOpen(false)}
        evaluation={selectedEvaluation}
      />
    </div>
  );
};

export default Dashboard;